import {
  keyframes,
  SerializedStyles,
  getFont,
  getBaseColor,
  getFocusableColor,
  getFontSize,
  BaseColor,
  Theme,
  forBrands,
  CSSObject,
} from "@core-ui/react-stitch";
import {DropdownStyleFn} from "../types";
import {arrow, nativeArrow} from "./arrow";

const getControlInverseThemeStyles = (theme: Theme) => ({
  backgroundColor: theme.color.gray80 ?? "#2C2824",
  color: theme.color.inverse.b1,
  "&:hover": {
    backgroundColor: "#403C38",
  },
  "&:disabled": {
    cursor: "default",
    opacity: ".6",
  },
});

const getControlPdpRedesignThemeStyles = (theme: Theme) => 
  forBrands(theme, {
    br: {
      color: theme.color.gray80,
      backgroundColor: theme.color.alpha00,
      "&:hover": {
        backgroundColor: "#E2E0D7",
      },
      "&:disabled": {
        cursor: "default",
        opacity: ".6",
      },
    },
    brfs: {
      color: theme.color.gray80,
      backgroundColor: theme.color.alpha00,
      "&:hover": {
        backgroundColor: "#E2E0D7",
      },
      "&:disabled": {
        cursor: "default",
        opacity: ".6",
      },
    },
  }) as CSSObject;

const getFontColor: DropdownStyleFn<BaseColor> = ({
  theme,
  crossBrand,
  disabled,
  inverse,
}) => {
  const defaultColor =
    !crossBrand && ["gap", "gapfs", "at"].includes(theme.brand)
      ? "gray80"
      : "bk";
  return getBaseColor(defaultColor)({theme, disabled, inverse});
};

const getHeight: DropdownStyleFn<string> = () => "2.25rem";

const movingBar: DropdownStyleFn = (props) => {
  const barColor = getFocusableColor("gray60")(props);
  const movingBarKeyframes = keyframes`
    0% {
      background-size: 1px 1px;
    }

    100% {
      background-size: 100% 1px;
    }
  `;

  const animation = !props.native
    ? {
        ":focus": {
          animation: `${movingBarKeyframes} 250ms linear forwards`,
        },
        ":hover": {
          animation: `${movingBarKeyframes} 250ms linear forwards`,
        },
      }
    : {};

  return {
    borderBottom: `1px solid ${barColor}`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "0 0",
    backgroundImage: `linear-gradient(
      to left,
      transparent 0%,
      ${barColor} 0%,
      ${barColor} 100%,
      transparent 100%
    )`,
    backgroundPosition: "bottom center",
    ...animation,
  };
};

const borderStyle: DropdownStyleFn<SerializedStyles | false | undefined> = ({
  hasError,
  ...props
}) =>
  hasError
    ? {
        paddingLeft: "0.25rem",
        border: `1px solid ${getBaseColor("err1")(props)}`,
      }
    : {};

const arrowStyle: DropdownStyleFn = ({native, ...props}) =>
  native ? nativeArrow(props) : {"&::before": arrow(props)};

export const controlCSS: DropdownStyleFn = (props) => {
  const {theme, crossBrand, inverse, enabledFeatures} = props;
  const showPdpRedesign =
    inverse !== true &&
    (enabledFeatures?.["cat-br-redesign"] ||
      enabledFeatures?.["pdp-redesign-2022"]);
  return {
    /* clear default button styling */
    "&:hover": {
      backgroundColor: theme.color.gray10,
    },
    appearance: "none",
    border: "none",
    borderRadius: 0,
    outline: "none",

    position: "relative",
    boxSizing: "border-box",
    padding: "6px 0",
    textAlign: "left",
    width: "100%",
    color: getFontColor(props),
    backgroundColor: theme.color.wh,
    ...getFont("primary")({theme, crossBrand}),
    fontSize: getFontSize({theme, crossBrand}),
    height: getHeight({theme, crossBrand}),
    textTransform: "none",
    marginBottom: 0,
    ...movingBar(props),
    ...borderStyle(props),
    ...arrowStyle(props),
    /* to prevent long text from wrapping and overlapping */
    whiteSpace: "nowrap",
    overflow: "hidden",
    ...(inverse ? getControlInverseThemeStyles(theme) : {}),
    ...(showPdpRedesign ? getControlPdpRedesignThemeStyles(theme) : {}),
  };
};
